import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSession } from '../state/session.tsx';
import type { BusinessSummary } from '../lib/types.ts';

const REGIME_LABELS: Record<string, string> = {
  IN_GST: 'GST',
  AE_VAT: 'VAT',
  GENERIC: 'Tax',
  NONE: 'No tax',
};

/** Two letters from the business name — a wordmark, not a generic avatar. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '··';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
}

export function BusinessSwitcher() {
  const { businesses, business, switchBusiness } = useSession();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!business) return null;

  const choose = (entry: BusinessSummary) => {
    setOpen(false);
    if (entry.id === business.id) return;
    switchBusiness(entry.id);
    // Ids are per-business, so a detail route would 404 after switching.
    navigate('/');
  };

  return (
    <div className="switcher" ref={containerRef}>
      <button
        type="button"
        className="switcher__trigger"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="switcher__mark" style={{ ['--mark-color' as string]: business.brandColor }}>
          {initials(business.name)}
        </span>
        <span className="switcher__text">
          <span className="switcher__name">{business.name}</span>
          <span className="switcher__meta">
            {business.currency} · {REGIME_LABELS[business.taxRegime] ?? business.taxRegime}
          </span>
        </span>
        <span className="switcher__caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="switcher__menu" role="menu">
          {businesses.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="menuitem"
              className="switcher__option"
              aria-current={entry.id === business.id}
              onClick={() => choose(entry)}
            >
              <span
                className="switcher__mark"
                style={{ ['--mark-color' as string]: entry.brandColor }}
              >
                {initials(entry.name)}
              </span>
              <span className="switcher__text">
                <span className="switcher__name">{entry.name}</span>
                <span className="switcher__meta">
                  {entry.currency} · {REGIME_LABELS[entry.taxRegime] ?? entry.taxRegime} ·{' '}
                  {entry.role}
                </span>
              </span>
              {entry.id === business.id && (
                <span className="switcher__check" aria-hidden="true">
                  ✓
                </span>
              )}
            </button>
          ))}

          <div className="switcher__divider" />
          <button
            type="button"
            role="menuitem"
            className="switcher__option"
            onClick={() => {
              setOpen(false);
              navigate('/settings/businesses/new');
            }}
          >
            <span className="switcher__mark" style={{ ['--mark-color' as string]: 'var(--ink-faint)' }}>
              +
            </span>
            <span className="switcher__text">
              <span className="switcher__name">Add a business</span>
              <span className="switcher__meta">Separate books, own numbering</span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
